import React from "react";
import { Link } from "react-router-dom";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import { stackSampleIntakeUrl } from "../../lib/workflowLinks";
import { useAuth, hasPermission } from "../../context/AuthContext";
import { PageHeader, KpiTile, SectionCard, StatePill } from "../../components/Ui";
import BoilerRuntimeSummary from "../../components/production/BoilerRuntimeSummary";
import { AlertTriangle, PackageOpen, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/button";

export default function SupervisorDashboard() {
  const { user } = useAuth();
  const { data, reload } = usePageBootstrap({
    path: "/dashboard/bootstrap",
    wsTypes: ["purchase", "stack"],
  });
  useBootstrapWS(["purchase", "stack", "production_monitoring"], reload);

  const unloading = data?.unloading || [];
  const approved = data?.approved || [];
  const supervisorTodos = data?.contract_todos || [];

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="supervisor-dashboard">
      <PageHeader
        eyebrow="Production Supervisor"
        title="Floor Desk"
        description="Unloading assignments, stock contract samples, and trucks cleared for unload."
        actions={
          <Button asChild variant="outline" className="min-h-11">
            <Link to="/unloading"><PackageOpen className="h-4 w-4 mr-2" />Unloading</Link>
          </Button>
        }
      />

      {supervisorTodos.length > 0 && (
        <div className="border-l-4 border-teal-600 bg-teal-50 rounded-r-xl px-4 py-4" data-testid="supervisor-todo-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-teal-800 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-teal-900">
                {supervisorTodos.length} stock contract{supervisorTodos.length > 1 ? "s" : ""} need sample analysis
              </div>
              <ul className="mt-2 space-y-1 text-sm text-teal-900/90">
                {supervisorTodos.slice(0, 4).map((t) => (
                  <li key={t.id}>
                    <span className="font-mono">{t.contract_number}</span> · {t.quality} · {t.quantity} qtl
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2 mt-3">
                {hasPermission(user, "page.stack_samples") && (
                  <Link to={stackSampleIntakeUrl()} className="text-xs uppercase tracking-widest text-teal-900 underline">
                    Register samples
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <KpiTile label="Unloading" value={unloading.length} sub="On the floor" accent testId="sup-kpi-unload" />
        <KpiTile label="Ready to unload" value={approved.length} sub="Approved trucks" testId="sup-kpi-approved" />
        <KpiTile label="Stock samples" value={supervisorTodos.length} sub="Contracts pending" testId="sup-kpi-samples" />
      </div>

      <SectionCard title="Active unloading" testId="sup-unloading-queue">
        {unloading.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No trucks currently unloading.</p>
        ) : (
          <div className="space-y-2">
            {unloading.slice(0, 8).map((p) => (
              <Link
                key={p.id}
                to={`/purchases/${p.id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-3 hover:bg-accent/60"
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm">{p.code}</div>
                  <div className="text-sm truncate">{p.party_name}</div>
                </div>
                <StatePill state={p.state} />
              </Link>
            ))}
          </div>
        )}
        <Link to="/unloading" className="inline-flex items-center gap-1 text-xs link-brand mt-3">
          Record unloading <ArrowRight className="h-3 w-3" />
        </Link>
      </SectionCard>

      <BoilerRuntimeSummary />
    </div>
  );
}
